import { applicableSections, applicableTemplates } from './classifier.js';
import { QUESTIONS } from './questions.js';
import { SECTIONS } from './sections.js';
/**
 * Build the renderable schema for a given classifier.
 *
 * This returns all questions whose section is applicable to the classifier.
 * `showIf` predicates are NOT evaluated here — they're applied per-render
 * by `visibleQuestions()` with the current in-form answers. This keeps the
 * engine simple and makes section progress computation correct (a question
 * that would become visible once the user answers "aluminum" on an earlier
 * field is counted in the section's potential scope).
 *
 * See docs/property/PRD.md §11 for the schema engine spec.
 */
export function buildSchema(classifier) {
    const applicableIds = new Set(applicableSections(classifier));
    const sections = SECTIONS.filter((s) => applicableIds.has(s.id));
    const questions = QUESTIONS.filter((q) => applicableIds.has(q.section));
    const templates = applicableTemplates(classifier);
    return {
        sections,
        questions,
        templates,
    };
}
/**
 * Given a built schema and current answers, return only the questions that
 * should render right now. Use this in the web form's render loop.
 */
export function visibleQuestions(schema, classifier, answers) {
    return schema.questions.filter((q) => {
        if (!q.showIf)
            return true;
        try {
            return q.showIf(classifier, answers);
        }
        catch {
            return false;
        }
    });
}
/**
 * Partition visible questions by their section, preserving question order
 * within each section.
 */
export function groupBySection(schema, classifier, answers) {
    const visible = visibleQuestions(schema, classifier, answers);
    const grouped = new Map();
    for (const section of schema.sections) {
        grouped.set(section.id, []);
    }
    for (const q of visible) {
        const bucket = grouped.get(q.section);
        if (bucket)
            bucket.push(q);
    }
    return grouped;
}
export function computeSectionProgress(schema, classifier, answers) {
    const grouped = groupBySection(schema, classifier, answers);
    const out = [];
    for (const section of schema.sections) {
        const qs = grouped.get(section.id) ?? [];
        let answered = 0;
        let required = 0;
        let requiredAnswered = 0;
        for (const q of qs) {
            const isAnswered = hasAnswer(answers[q.id]);
            if (isAnswered)
                answered++;
            if (q.required) {
                required++;
                if (isAnswered)
                    requiredAnswered++;
            }
        }
        out.push({
            sectionId: section.id,
            total: qs.length,
            answered,
            required,
            requiredAnswered,
            complete: required === 0 ? answered === qs.length : required === requiredAnswered,
        });
    }
    return out;
}
function hasAnswer(value) {
    if (value === null || value === undefined)
        return false;
    if (typeof value === 'string')
        return value.trim().length > 0;
    if (Array.isArray(value))
        return value.length > 0;
    if (typeof value === 'object')
        return Object.keys(value).length > 0;
    return true; // numbers, booleans etc.
}
//# sourceMappingURL=schemaEngine.js.map